import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IsActiveMatchOptions, Router } from '@angular/router';

import { signOut } from 'firebase/auth';
import { documentId, query, where } from 'firebase/firestore';
import { combineLatest, firstValueFrom, map, Observable, shareReplay } from 'rxjs';

import { ClientsDao } from '../../../data/daos/clients.dao';
import { UsersDao } from '../../../data/daos/users.dao';
import { WithDbMeta } from '../../../data/models/base.model';
import { Client } from '../../../data/models/clients.model';
import { User } from '../../../data/models/user.model';
import { fmtRoute, parseFmtUrl } from '../../../portal/financial-modeling/fmt-route.util';
import { AddEditUserComponent, AddEditUserResponse } from '../../../portal/users/add-edit-user/add-edit-user.component';
import { DIALOG_WIDTH } from '../../constants/dialog';
import { AppRoute, FmtPage } from '../../constants/route';
import { FirebaseService } from '../../services/firebase.service';
import { LocalStorageUtil } from '../../utils/local-storage.util';
import {
	buildNavContext, getClientChangeNavigation, getDefaultPageForTool, getSectionsForTool, getVisibleTools,
	NAV_TOOL_CONFIGS, NavPage, NavSection, NavTool, NavToolConfig,
} from './nav.config';
import { ResetPasswordDialogComponent } from './reset-password-dialog/reset-password-dialog.component';
import { SideNavService } from './side-nav.service';

interface SideNavClientVM {
	activeClient: WithDbMeta<Client>;
	visibleTools: NavToolConfig[];
	sectionsForSelectedTool: NavSection[];
}

@Component({
	selector: 'dix-side-nav',
	templateUrl: './side-nav.component.html',
	styleUrls: ['./side-nav.component.scss'],
	standalone: false,
})
export class SideNavComponent {
	NavTool = NavTool;
	readonly routerLinkActiveOptions: IsActiveMatchOptions = {
		paths: 'exact',
		queryParams: 'ignored',
		fragment: 'ignored',
		matrixParams: 'ignored',
	};

	readonly sideNav = inject(SideNavService);

	private readonly clientsDao = inject(ClientsDao);
	private readonly router = inject(Router);
	private readonly auth = inject(FirebaseService).auth;
	private readonly dialog = inject(MatDialog);
	private readonly userDao = inject(UsersDao);

	readonly clientVm: Observable<SideNavClientVM> = combineLatest([
		this.clientsDao.streamActiveClient(),
		this.sideNav.selectedTool$,
		this.sideNav.reportPages$,
	]).pipe(
		map(([activeClient, tool, reportPages]) => {
			const ctx = buildNavContext(activeClient);
			return {
				activeClient,
				visibleTools: getVisibleTools(ctx),
				sectionsForSelectedTool: getSectionsForTool(tool, ctx, reportPages),
			};
		}),
	);

	readonly clients$: Observable<WithDbMeta<Client>[]> = this.clientsDao
		.collection()
		.streamAll((ref) =>
			LocalStorageUtil.getIsInternal() ? ref : query(ref, where(documentId(), 'in', LocalStorageUtil.getClientIds())),
		)
		.pipe(
			map((clients) => clients.filter((client) => !client.hideInWebsite)),
			shareReplay(1),
		);

	readonly currentUser$: Observable<WithDbMeta<User>> = this.userDao.streamUser(LocalStorageUtil.getUserId());

	async onToolClick(tool: NavTool) {
		const client = (await firstValueFrom(this.clientVm)).activeClient;
		const user = await firstValueFrom(this.currentUser$);
		this.sideNav.selectTool(tool);
		void this.router.navigate(getDefaultPageForTool(tool, client, user));
	}

	isToolSelected(tool: NavTool): boolean {
		return this.sideNav.selectedTool === tool;
	}

	shouldShowClientSelector(): boolean {
		if (this.sideNav.selectedTool !== NavTool.Manage) {
			return true;
		}
		const first = this.router.url.split('?')[0].split('/').filter(Boolean)[0] as AppRoute;
		return first === AppRoute.Users;
	}

	getSelectedToolLabel(): string {
		return NAV_TOOL_CONFIGS.find((t) => t.tool === this.sideNav.selectedTool)?.expandedTitle ?? '';
	}

	async onClientChange(selectedClientId: string): Promise<void> {
		LocalStorageUtil.setActiveClientId(selectedClientId);
		const client = await this.clientsDao.doc(selectedClientId).get();
		const pathSegments = getClientChangeNavigation(client, this.router.url);
		await this.router.navigate(pathSegments);
		window.location.reload();
	}

	async logout() {
		await signOut(this.auth);
	}

	fmtPageRoute(page: NavPage): string[] {
		const pageSegment = page.route.at(-1) as FmtPage;
		const scenarioId = parseFmtUrl(this.router.url).scenarioId ?? '_';
		return fmtRoute(scenarioId, pageSegment);
	}

	pageRoute(page: NavPage): string[] {
		if (this.sideNav.selectedTool === NavTool.Finance) {
			return this.fmtPageRoute(page);
		}
		return page.route;
	}

	changePassword() {
		this.dialog.open(ResetPasswordDialogComponent, {
			autoFocus: false,
			width: '400px',
			height: '450px',
		});
	}

	async editProfile() {
		const currentUser = await firstValueFrom(this.currentUser$);
		const modal = this.dialog.open<
			AddEditUserComponent,
			{ existing: WithDbMeta<User>; id: string },
			AddEditUserResponse
		>(AddEditUserComponent, {
			autoFocus: false,
			width: DIALOG_WIDTH,
			data: { existing: currentUser, id: LocalStorageUtil.getUserId() },
		});
		const data = await firstValueFrom(modal.afterClosed());
		if (data.user) {
			await this.userDao.update(data.id, data.user);
		}
	}
}
